import React from 'react'
import clsx from 'clsx'
import { Controller, type FieldValues, type Path } from 'react-hook-form'

import { TextInput } from '@/components/widget-components'

export const FieldError = React.memo(function FieldError({
  message,
  id,
  reserveSpace = true,
}: {
  message?: string
  id: string
  reserveSpace?: boolean
}) {
  if (!message && !reserveSpace) {
    return null
  }

  return (
    <span
      id={id}
      role="alert"
      className={clsx(
        'block text-red-600 text-xs mt-1 leading-tight',
        reserveSpace && 'min-h-[1.25rem]'
      )}
      data-testid={id}
    >
      {message ?? ''}
    </span>
  )
})

function TextFieldComponent<T extends FieldValues>({
  name,
  placeholder,
  testId,
  required,
  autoFocus,
  format,
}: {
  name: Path<T>
  placeholder: string
  testId: string
  required?: boolean
  autoFocus?: boolean
  format?: (value: string) => string
}) {
  const errorId = `${testId}-error`
  return (
    <Controller<T>
      name={name}
      render={({ field, fieldState }) => (
        <div>
          <TextInput
            id={testId}
            placeholder={placeholder}
            aria-label={placeholder}
            aria-invalid={!!fieldState.error}
            aria-describedby={fieldState.error ? errorId : undefined}
            value={String(field.value ?? '')}
            onChange={(e) =>
              field.onChange(format ? format(e.target.value) : e.target.value)
            }
            onBlur={field.onBlur}
            data-testid={testId}
            required={required}
            autoFocus={autoFocus}
          />
          <FieldError message={fieldState.error?.message} id={errorId} />
        </div>
      )}
    />
  )
}

// Cast preserves the generic call signature that React.memo would otherwise erase.
export const TextField = React.memo(
  TextFieldComponent
) as typeof TextFieldComponent
